// A board square's position on screen, kept current without re-rendering for
// nothing. The board-anchored half of PLAN-UI §28.1's two primitives.
//
// **This pattern existed in hand-rolled copies** — `PieceOverlays`,
// `BoardLabels`, `useHintAnchor` — and each copy re-derived
// the same four rules, one of which each copy states in its own words:
//
// 1. the varying inputs live in a **ref mirror**, so the frame callback is
//    registered once and never closes over a stale square. Re-subscribing per
//    change would churn the renderer's listener set every frame the camera
//    moves;
// 2. the subscription is `onFrame` and **never a private
//    `requestAnimationFrame`** — two rAF callbacks run in registration order,
//    so an overlay polling its own frame projects against either side of the
//    renderer's update depending on the frame, and that inconsistency is what
//    reads as shaking;
// 3. a **direct call on every change** as well. The frame callback cannot fire
//    before the renderer exists — it is created in an effect, a commit after
//    the handle — and a subscriber that arrives in that window would otherwise
//    sit unplaced;
// 4. an **epsilon check before `setState`** (`samePoint`), or a camera that has
//    settled re-renders every consumer on every frame with the same numbers.
//
// The hook takes a *square* and never a point: projecting belongs to the
// renderer and has to be redone per frame, so a point computed anywhere else
// is stale the moment the board turns.

import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

import { type BoardAnchorPort, type Point, samePoint } from "./anchor";

/** Which of the two projections to take. See `BoardAnchorPort`. */
export type BoardPointMode = "centre" | "corner";

/**
 * Where `square` is on screen right now, or null when there is nothing to
 * anchor to — no square, or no renderer yet.
 *
 * **Null, never a point off screen.** The handle answers `{-1000, -1000}` with
 * no renderer, which is a position a caller can position against and get
 * wrong; a consumer of this hook draws nothing until there is somewhere real
 * to draw.
 */
export function useBoardPoint(
  board: RefObject<BoardAnchorPort | null>,
  square: number | null,
  mode: BoardPointMode = "centre"
): Point | null {
  const [point, setPoint] = useState<Point | null>(null);

  // Rule 1. Written on every render rather than in an effect: the frame
  // callback may run before an effect commits, and it must never read the
  // render before last.
  const latest = useRef({ square, mode });
  latest.current = { square, mode };

  const project = useCallback(() => {
    const handle = board.current;
    const { square: sq, mode: how } = latest.current;
    // Called as a method, never destructured off the handle: `projectSquare`
    // is the board's own function and detaching it is one refactor away from
    // losing `this`.
    const next =
      handle && sq !== null
        ? how === "centre"
          ? handle.projectCentre(sq)
          : handle.projectSquare(sq)
        : null;
    // Rule 4.
    setPoint((was) => (samePoint(was, next) ? was : next));
  }, [board]);

  // Rule 2. `project` is stable, so this subscribes once per board.
  useEffect(() => board.current?.onFrame(project), [board, project]);

  // Rule 3.
  useEffect(() => project(), [project, square, mode]);

  return point;
}
